import {QueryBuilderColumn, QueryBuilderStateQuery, QueryBuilderTemplate, ThemeMixin} from "../../../index";
import React, {FunctionComponent, useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../hooks/use-is-mounted";
import axios, {CancelToken} from "axios";
import QueryBuilderUtils from "../../../core/services/utils";
import QueryBuilderApiService, {CreateTemplateRequestDS, ReplaceTemplateRequestDS} from "../../../core/services/api";
import {Box, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Skeleton, Theme, Typography, Unstable_Grid2 as Grid} from "@mui/material";
import Form, {FinalForm, MuiRFF, Yup} from "../form";
import {QueryBuilderProps} from "../../index";
import {useQueryBuilderContext} from "../provider";

export interface QueryBuilderSaveTemplateProps {
    query: QueryBuilderStateQuery,
    columns: QueryBuilderColumn[],
    name: QueryBuilderProps['name'];
}

const QueryBuilderSaveTemplate: FunctionComponent<QueryBuilderSaveTemplateProps> = (props) => {
    const {applicationName} = useQueryBuilderContext();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [replacing, setReplacing] = useState(false);
    const [creating, setCreating] = useState(false);
    const [templates, setTemplates] = useState<Array<QueryBuilderTemplate>>([]);
    const isMounted = useIsMounted();

    /**
     * With each change in the [open] state:
     * - if the dialog is closed, reset the templates.
     * - if the dialog is closed, set the loading state to true.
     */
    useEffect(() => {
        if (open) {
            setLoading(true);
            setTemplates([]);
            setCreating(false);
        }
    }, [open]);

    /**
     * Fetches the query builder templates from the server.
     */
    const fetchTemplates = useCallback(async (cancelToken: CancelToken) => {
        if (!props.name?.length)
            return;
        setLoading(true);
        const response = await QueryBuilderApiService.getTemplates(applicationName, props.name, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoading(false);
        }
        if (response?.resultFlag && !response?.aborted) {
            setTemplates(response.data ?? []);
        }
    }, [isMounted, props.name, applicationName]);

    /**
     * As soon as the component is mounted and with each change in the [name] prop:
     * - fetch the templates for the query builder.
     */
    useEffect(() => {
        if (!open)
            return;
        const axiosCancelTokenSource = axios.CancelToken.source();
        fetchTemplates(axiosCancelTokenSource.token).then();
        return () => axiosCancelTokenSource.cancel();
    }, [fetchTemplates, open]);


    /**
     * Opens the dialog.
     */
    const openDialog = useCallback(() => {
        setOpen(true);
    }, []);

    /**
     * Closes the dialog if the values from the state do not prevent the closing of the dialog.
     */
    const onDialogClosed = useCallback(() => {
        setOpen(false);
    }, [])


    const schema = useMemo(() => Yup.object({
        title: Yup.string()
            .required("this field is required")
            .trim(),
    }), []);

    interface Schema extends Yup.InferType<typeof schema> {
    }

    type SchemaKeys = keyof Schema;

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo<Record<SchemaKeys, boolean>>(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Submits the form values to save a new template.
     */
    const createTemplate = useCallback<FinalForm.Config<Schema>["onSubmit"]>(async (values, form) => {
        const forApi: CreateTemplateRequestDS = {
            applicationName: applicationName,
            templates: templates,
            name: props.name ?? "",
            newTemplate: {
                title: values.title,
                data: QueryBuilderUtils.siftQueryWithColumns(props.query, props.columns, QueryBuilderUtils.mapStateQueryRuleToQueryRule)
            },
        }
        setCreating(true);
        const response = await QueryBuilderApiService.createTemplate(forApi);
        if (!isMounted())
            return;
        setCreating(false);
        if (response?.resultFlag && !response?.aborted) {
            setTemplates(prevState => [...prevState, response.data!]);
            form.change('title', '')
            form.resetFieldState('title');
            setOpen(false);
        }
    }, [templates, props.name, props.query, props.columns, isMounted, applicationName]);

    /**
     * Replaces the template with the current query.
     */
    const replaceTemplate = useCallback(async (template: QueryBuilderTemplate) => {
        const forApi: ReplaceTemplateRequestDS = {
            applicationName: applicationName,
            templates: templates,
            name: props.name ?? "",
            templateId: template.id,
            data: QueryBuilderUtils.siftQueryWithColumns(props.query, props.columns, QueryBuilderUtils.mapStateQueryRuleToQueryRule),
        }
        setReplacing(true);
        const response = await QueryBuilderApiService.replaceTemplate(forApi);
        if (!isMounted())
            return;
        setReplacing(false);
        if (response?.resultFlag) {
            setTemplates(prevState => prevState.map((e) => e.id === forApi.templateId ? {...e, data: forApi.data} : e));
            setOpen(false);
        }
    }, [isMounted, props.columns, props.name, props.query, templates, applicationName]);

    /**
     * Creates the styles for the container boxes.
     * @param {Theme} theme                      The theme to use
     * @returns {ReturnType<ThemeMixin<Theme>>}  The styles for the container boxes
     */
    const boxMixin = useCallback<ThemeMixin<Theme>>((theme) => ({
        backgroundColor: theme.palette.background.tertiary,
        borderRadius: theme.vars?.shape.borderRadius,
        padding: 2,
        marginBlock: 2,
    }), []);

    return (
        <>
            <Button
                variant={'outlined'}
                color={'primary'}
                onClick={openDialog}
                size={'medium'}
                sx={{paddingBlock: 0.5, paddingInline: 2, minWidth: 150}}
            >
                Save Filters as Template
            </Button>
            <Dialog
                maxWidth={"sm"}
                fullWidth
                onClose={onDialogClosed}
                open={open}
            >
                <DialogTitle fontWeight={600}>
                    Save Template
                </DialogTitle>
                <DialogContent>
                    {/*@ts-ignore*/}
                    <Form<Schema, Partial<Schema>>
                        onSubmit={createTemplate}
                        validate={validate}
                    >
                        <Box sx={boxMixin}>
                            <Typography variant={'subtitle2'} fontWeight={600}>
                                Save a new template
                            </Typography>
                            <Grid container xs={12} gap={2} sx={{marginBlock: 2, marginInline: 0}}>
                                <Grid xs padding={0}>
                                    <MuiRFF.TextField
                                        fullWidth
                                        size={'small'}
                                        name={"title"}
                                        label={"Title"}
                                        required={required.title}
                                    />
                                </Grid>
                                <Grid xs={'auto'} padding={0}>
                                    <Button
                                        variant={'contained'}
                                        type={'submit'}
                                        color={'primary'}
                                        size={'small'}
                                        disabled={creating}
                                        sx={{padding: '4px 10px'}}
                                    >
                                        {
                                            creating
                                                ? "Saving..."
                                                : "Save"
                                        }
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Form>
                    <Divider
                        orientation="horizontal"
                        variant="middle"
                        sx={{marginBlock: 2, '&:before, &:after': {borderTopStyle: 'dashed',}}}
                    >
                        <Typography variant={'body2'} fontWeight={600}>
                            OR Replace Existing Template
                        </Typography>
                    </Divider>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                        {
                            loading &&
                            Array.from({length: 4}).map((_, key) => (
                                <Skeleton
                                    key={key}
                                    variant="rectangular"
                                    width={'100%'}
                                    sx={(theme) => ({minHeight: 40, borderRadius: (theme as any).vars?.shape.borderRadius})}
                                />
                            ))
                        }
                        {
                            !loading && !templates.length &&
                            <Typography variant={'body2'} textAlign={'center'} sx={{marginBlock: 3}}>
                                No templates found.
                            </Typography>
                        }
                        {
                            !loading &&
                            templates.map((template) => (
                                // @ts-ignore
                                <Card
                                    key={template.id}
                                    value={template.id}
                                    variant={'outlined'}
                                    sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, padding: 2}}
                                >
                                    <Typography variant={'body1'} fontWeight={500}>
                                        {template.title}
                                    </Typography>
                                    <Button
                                        size={'small'}
                                        variant={'text'}
                                        disabled={replacing}
                                        onClick={() => replaceTemplate(template)}
                                    >
                                        {
                                            replacing
                                                ? "Saving..."
                                                : "Save"
                                        }
                                    </Button>
                                </Card>
                            ))
                        }
                    </Box>
                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between'}}>
                    <Button
                        variant={'text'}
                        type={'button'}
                        color={'primary'}
                        onClick={onDialogClosed}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default QueryBuilderSaveTemplate;
